import { ChangesDetection } from "@adas/ng-overlay";
import { Observable, startWith, map, distinctUntilChanged, switchMap, of, shareReplay, combineLatest } from "rxjs";
import { I_AlarmResponse } from "../../../../alarm/api/alarm-config.response";
import { C_AlarmConfig } from "../../../../alarm/domain/alarm-config.model";
import { C_PatientData } from "../../patient/domain/patient.model";

export class C_AlarmList extends ChangesDetection<T_AlarmChange> {
  private list: Map<string, C_ObservationAlarmData> = new Map();
  private globalAlarmIds: Set<string> = new Set();
  private isInitialized: boolean = false;
  private cachedSubs = new Map<string, Observable<C_ObservationAlarmData | undefined>>();

  constructor(data?: I_AlarmResponse[]) {
    super();
    if (data) this.initializeArray(data);
  }

  initializeArray(data: I_AlarmResponse[]) {
    const bulkSet = new Set<string>();
    data.forEach((item) => {
      const alarmId = `${item.patientId}_${item.type}_${item.value}`;
      if (!alarmId) return;
      bulkSet.add(alarmId);
      const existing = this.list.get(alarmId);
      if (existing) {
        existing.initialize(item);
      } else {
        this.list.set(alarmId, new C_ObservationAlarmData(item));
      }
      if (item.isBigPicture) {
        this.globalAlarmIds.add(alarmId);
      }
    });
    this.isInitialized = true;
    this.forceUpdateDetection({ action: E_AlarmChangeAction.Bulk, ids: bulkSet, data });
  }

  initializeArrayFromAlarms(data: C_ObservationAlarmData[]) {
    const bulkSet = new Set<string>();
    data.forEach((item) => {
      const alarmId = `${item.patientId}_${item.type}_${item.value}`;
      if (!alarmId) return;
      bulkSet.add(alarmId);
      const existing = this.list.get(alarmId);
      if (existing) {
        existing.initializeFromAlarm(item);
      } else {
        this.list.set(alarmId, item);
      }
      if (item.isBigPicture) {
        this.globalAlarmIds.add(alarmId);
      }
    });
    this.isInitialized = true;
    this.forceUpdateDetection({ action: E_AlarmChangeAction.Bulk, ids: bulkSet, data: data.map(d => d.toInterface()) });
  }

  isInit(): boolean {
    return this.isInitialized;
  }

  get values(): C_ObservationAlarmData[] {
    return Array.from(this.list.values());
  }

  getAlarm(alarmId: string): C_ObservationAlarmData | undefined {
    return this.list.get(alarmId);
  }

  upsertAlarm(data: I_AlarmResponse) {
    const id = `${data.patientId}_${data.type}_${data.value}`;
    if (!id) return;

    const action = this.list.has(id) ? E_AlarmChangeAction.Update : E_AlarmChangeAction.Add;
    const isGlobal = !!data.isBigPicture;
    if (action === E_AlarmChangeAction.Update) {
      this.list.get(id)?.initialize(data);
    } else {
      this.list.set(id, new C_ObservationAlarmData(data));
    }
    if (isGlobal) {
      this.globalAlarmIds.add(id);
    } else {
      this.globalAlarmIds.delete(id);
    }
    this.forceUpdateDetection({ action, id, data });
  }

  getLiveAlarm(alarmId: string): Observable<C_ObservationAlarmData | undefined> {
    if (this.cachedSubs.has(alarmId)) return this.cachedSubs.get(alarmId)!;

    const stream$ = this.getForcedChanges().pipe(
      startWith(null),
      map(() => this.getAlarm(alarmId)),
      distinctUntilChanged(),
      switchMap(alarm => {
        if (!alarm) return of(undefined);
        return alarm.getForcedChanges().pipe(
          startWith(null),
          map(() => alarm)
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.cachedSubs.set(alarmId, stream$);
    return stream$;
  }

  get globalAlarms(): C_ObservationAlarmData[] {
    const globals: C_ObservationAlarmData[] = [];
    this.globalAlarmIds.forEach(id => {
      const alarm = this.list.get(id);
      if (alarm) globals.push(alarm);
    });
    return globals;
  }


  getLiveGlobalAlarms(): Observable<C_ObservationAlarmData[]> {
    return this.getForcedChanges().pipe(
      startWith(null),
      map(() => this.globalAlarms),
      switchMap((alarms) => {
        if (alarms.length === 0) return of([]);

        // Escuchamos las mutaciones internas de CADA alarma global
        const alarmStreams$ = alarms.map((alarm) =>
          alarm.getForcedChanges().pipe(
            startWith(null),
            map(() => alarm)
          )
        );

        return combineLatest(alarmStreams$);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  deleteAlarm(data: C_ObservationAlarmData) {
    const id = `${data.patientId}_${data.type}_${data.value}`;
    if (this.list.delete(id)) {
      this.cachedSubs.delete(id);
      this.globalAlarmIds.delete(id);
      this.forceUpdateDetection({ action: E_AlarmChangeAction.Delete, id });
    }
  }

  clearSubs() {
    this.cachedSubs.clear();
  }
}

export type T_AlarmChange =
  | { action: E_AlarmChangeAction.Add; id: string; data: I_AlarmResponse }
  | { action: E_AlarmChangeAction.Update; id: string; data: I_AlarmResponse }
  | { action: E_AlarmChangeAction.Bulk, ids: Set<string>; data: I_AlarmResponse[] }
  | { action: E_AlarmChangeAction.Delete; id: string; };

export interface I_InactivationStateResponse {
  audio?: string;
  visual?: string;
  acknowledge?: boolean;
}

export class C_ObservationAlarmData extends ChangesDetection {
  id: string = '';
  type?: string;
  priority?: string;
  priorityLevel?: number;
  state?: string;
  event?: string;
  eventPhase?: string;
  inactivationState?: C_InactivationState;
  color?: string;
  value?: string;
  time?: string;
  name?: string;
  iconCategory?: string;
  iconName?: string;
  alarmConfig?: C_AlarmConfig;
  expires?: number;
  patientId?: string;
  pointOfCareId?: string;
  isBigPicture?: boolean;
  patient?: C_PatientData;
  constructor(json?: I_AlarmResponse) {
    super();
    if (json) this.initialize(json);
  }

  initialize(json: I_AlarmResponse) {
    if (json.id) this.id = json.id;
    if (json.patientId) this.patientId = json.patientId;
    if (json.type) this.type = json.type;
    if (json.priority) this.priority = json.priority;
    if (json.priorityLevel) this.priorityLevel = json.priorityLevel;
    if (json.state) this.state = json.state;
    if (json.event) this.event = json.event;
    if (json.pointOfCareId) this.pointOfCareId = json.pointOfCareId;
    if (json.eventPhase) this.eventPhase = json.eventPhase;
    if (json.isBigPicture !== undefined) this.isBigPicture = json.isBigPicture;
    if (json.iconCategory !== undefined) this.iconCategory = json.iconCategory;
    if (json.iconName !== undefined) this.iconName = json.iconName;
    if (json.inactivationState) {
      this.inactivationState = new C_InactivationState(json.inactivationState);
    }

    if (json.alarmConfig) {
      this.alarmConfig = new C_AlarmConfig(json.alarmConfig);
      this.color = json.alarmConfig.color;
    }

    if (json.value) this.value = json.value;
    if (json.time) this.time = json.time;
    if (json.name) this.name = json.name;
    if (json.expires) this.expires = json.expires;
    if (json.patient) this.patient = new C_PatientData(json.patient);
    this.forceUpdateDetection();
  }

  initializeFromAlarm(newElement: C_ObservationAlarmData) {
    if (newElement.id) this.id = newElement.id;
    if (newElement.patientId) this.patientId = newElement.patientId;
    if (newElement.type) this.type = newElement.type;
    if (newElement.priority) this.priority = newElement.priority;
    if (newElement.priorityLevel) this.priorityLevel = newElement.priorityLevel;
    if (newElement.state) this.state = newElement.state;
    if (newElement.event) this.event = newElement.event;
    if (newElement.pointOfCareId) this.pointOfCareId = newElement.pointOfCareId;
    if (newElement.eventPhase) this.eventPhase = newElement.eventPhase;
    if (newElement.isBigPicture !== undefined) this.isBigPicture = newElement.isBigPicture;

    if (newElement.inactivationState) {
      this.inactivationState = new C_InactivationState(newElement.inactivationState);
    }

    if (newElement.alarmConfig) {
      this.alarmConfig = newElement.alarmConfig;
      this.color = newElement.color;
    }

    if (newElement.value) this.value = newElement.value;
    if (newElement.time) this.time = newElement.time;
    if (newElement.name) this.name = newElement.name;
    if (newElement.expires) this.expires = newElement.expires;

    this.forceUpdateDetection();
  }

  public toInterface(): I_AlarmResponse {
    return {
      id: this.id,
      patientId: this.patientId,
      type: this.type,
      priority: this.priority,
      priorityLevel: this.priorityLevel,
      state: this.state,
      event: this.event,
      pointOfCareId: this.pointOfCareId,
      eventPhase: this.eventPhase,
      isBigPicture: this.isBigPicture,
      inactivationState: this.inactivationState ? this.inactivationState.toInterface() : undefined,
      alarmConfig: this.alarmConfig?.toInterface(),
      value: this.value,
      time: this.time,
      name: this.name,
      expires: this.expires,
    };
  }

  public updateValueFromClass(newElement: C_ObservationAlarmData) {
    if (newElement.type) this.type = newElement.type;
    if (newElement.priority) this.priority = newElement.priority;
    if (newElement.priorityLevel) this.priorityLevel = newElement.priorityLevel;
    if (newElement.state) this.state = newElement.state;
    if (newElement.event) this.event = newElement.event;
    if (newElement.eventPhase) this.eventPhase = newElement.eventPhase;
    if (newElement.inactivationState) this.inactivationState = new C_InactivationState(newElement.inactivationState);
    if (newElement.alarmConfig?.color) this.color = newElement.alarmConfig.color;
    if (newElement.value) this.value = newElement.value;
    if (newElement.time) this.time = newElement.time;
    if (newElement.name) this.name = newElement.name;
    if (newElement.alarmConfig) this.alarmConfig = newElement.alarmConfig;
    if (newElement.expires) this.expires = newElement.expires;
    if (newElement.isBigPicture !== undefined) this.isBigPicture = newElement.isBigPicture;
  }

  public isExpired(): boolean {
    if (this.time != null && this.expires != null) {
      const now = Date.now();
      const expiredAt = new Date(this.time).getTime() + (this.expires * 1000);
      return now > expiredAt;
    }
    return false;
  }
}

export class C_InactivationState {
  audio?: string;
  visual?: string;
  acknowledge?: boolean;
  constructor(data?: I_InactivationStateResponse) {
    if (!data) return;
    this.initialize(data);
  }

  initialize(data: I_InactivationStateResponse) {
    if (data.audio) this.audio = data.audio;
    if (data.visual) this.visual = data.visual;
    if (data.acknowledge) this.acknowledge = data.acknowledge;
  }

  toInterface(): I_InactivationStateResponse {
    return {
      audio: this.audio,
      visual: this.visual,
      acknowledge: this.acknowledge
    }
  }
}


//   enum AudioVideoState {
//     PAUSED,
//     OFF,
//     ENABLED,
//     DISABLED,
//   }

//   enum ObservationAlarmType {
//     SP,
//     ST,
//     SA,
//   }

//   enum ObservationAlarmPriority {
//     PH,
//     PM,
//     PL,
//   }

//   enum EventPhase {
//     TPOINT,
//     START,
//     CONTINUE,
//     END,
//     UPDATE,
//     ESCALATE,
//     DEESCALATE,
//     RESET,
//     INACTIVATION,
//   }

//   enum ObservationAlarmState {
//     INACTIVE,
//     ACTIVE,
//     LATCHED,
//   }



export enum E_AlarmChangeAction {
  Add = "add",
  Update = "update",
  Delete = "delete",
  Bulk = "bulk"
}
